/**
 * Public-facing labels for the two Siji Fixed Link products.
 *
 * Fund codes stay `SFFR` / `SFFD` on the wire (uploads, URLs, tests). This
 * module is the only place the dashboard translates those codes into the
 * product names stakeholders see.
 */

export const SFL_FUND_CODES = ['SFFR', 'SFFD'] as const;

export type SflFundCode = (typeof SFL_FUND_CODES)[number];

export type ComboFilter = SflFundCode | 'both';

/** Which growth metric(s) the combo chart plots — independently toggleable, at least one always on. */
export type ComboMetric = 'mom' | 'threeMonth' | 'yoy';

export const DEFAULT_COMBO_METRICS: readonly ComboMetric[] = ['mom', 'yoy'];

export interface SflProduct {
  code: SflFundCode;
  tabLabel: string;
  shortName: string;
  currency: 'IDR' | 'USD';
}

export const SFL_PRODUCTS: readonly SflProduct[] = [
  {
    code: 'SFFR',
    tabLabel: 'Siji Fixed Link (IDR)',
    shortName: 'SFL IDR',
    currency: 'IDR',
  },
  {
    code: 'SFFD',
    tabLabel: 'Siji Fixed Link (Dollar)',
    shortName: 'SFL Dollar',
    currency: 'USD',
  },
] as const;

export function isSflFundCode(code: string | null | undefined): code is SflFundCode {
  return code === 'SFFR' || code === 'SFFD';
}

export function sflProduct(code: string | null | undefined): SflProduct | undefined {
  if (!code) return undefined;
  return SFL_PRODUCTS.find((p) => p.code === code);
}

export function sflTabLabel(code: string | null | undefined): string {
  return sflProduct(code)?.tabLabel ?? code ?? '—';
}

export function sflShortName(code: string | null | undefined): string {
  return sflProduct(code)?.shortName ?? code ?? '—';
}
